// Foundations
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';

// Models
import { ReservationPetInfo } from '../../../models/reservation-pet-info.model';
import { ReservationServiceCategory } from 'src/app/models/reservation/reservation-service-category.model';
import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';
import { ReservationServiceItem } from 'src/app/models/reservation/reservation-service.model';

import * as lodash from 'lodash';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-reservation-add-ons',
  templateUrl: './reservation-add-ons.component.html',
  styleUrls: ['./reservation-add-ons.component.scss'],
})
export class ReservationAddOnsComponent implements OnInit, OnDestroy {
  // #region -> Property <-
  // ----------

  @Input() reservationRequest$: Observable<ReservationRequestItem>;
  reservationRequest: ReservationRequestItem = new ReservationRequestItem({});
  reservationRequestSubscription: Subscription;
  @Output() reservationRequestChange =
    new EventEmitter<ReservationRequestItem>();

  @Input() serviceCategories$: Observable<{
    [categoryId: string]: ReservationServiceCategory;
  }>;
  serviceCategories: { [categoryId: string]: ReservationServiceCategory } = {};
  serviceCategoriesSubscription: Subscription;

  @Output() navigateToNext = new EventEmitter();

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Form <-
  // ----------

  formGroup: FormGroup;
  get reservationNotes(): string {
    return this.formGroup.get('reservationNotes').value as string;
  }
  get addonServiceFormArray(): FormArray {
    return this.formGroup.get('addonServices') as FormArray;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property <-
  // ----------

  availableAddonServices: ReservationServiceItem[] = [];
  pets: ReservationPetInfo[] = [];

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this._initForm();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this.serviceCategoriesSubscription = this.serviceCategories$.subscribe(
      (newServiceCategories) => {
        if (lodash.isEmpty(newServiceCategories)) {
          this.serviceCategories = {};
        } else {
          this.serviceCategories = newServiceCategories;
        }
        this.resetFormData(this.reservationRequest);

        // Trigger change detection when mutated.
        this.changeDetectorRef.markForCheck();
      }
    );
    this.reservationRequestSubscription = this.reservationRequest$.subscribe(
      (newReservationRequest) => {
        this.reservationRequest = this.reservationRequest.update({
          ...newReservationRequest.json(),
        });

        // For an empty "addon services", reset the form data
        if (!this.reservationRequest.hasAddonServices()) {
          this.resetFormData(this.reservationRequest);
        }

        // Trigger change detection when mutated.
        this.changeDetectorRef.markForCheck();
      }
    );
  }

  ngOnDestroy(): void {
    //
    this.reservationRequestSubscription.unsubscribe();
    this.serviceCategoriesSubscription.unsubscribe();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------

  getBreedNameList(pet): string {
    const breedStrings = pet.breeds
      ? pet.breeds.map((breed) => breed.breed.label)
      : [];
    if (breedStrings.length > 0) {
      return breedStrings.join(', ');
    } else {
      return 'unknown';
    }
  }

  togglePet(index: number): void {
    const element = this.addonServiceFormArray.at(index);
    if (element.value.isSelected) {
      // About to de-select, we need to clear all selections.
      this.reservationRequest = this.reservationRequest.removeAddonService(
        element.value.id
      );
    }

    this.addonServiceFormArray.at(index).patchValue({
      isSelected: !this.addonServiceFormArray.at(index).value.isSelected,
    });
  }

  addonServiceUpdated(event): void {
    this.reservationRequest = this.reservationRequest.update({
      ...event.json(),
    });
  }

  /**
   * Check if the "Save" button can be used.
   *
   * Rules:
   * - By default, the user can still submit their reservation "without" any add-on
   * - However, if the user select one, but it has some issue, then the user cannot submit.
   */
  isSaveEnabled(): boolean {
    // Seems that all add-ons are optional
    return this.reservationRequest.isValidForAddonServices();
  }

  onSave(): void {
    //
    this.reservationRequest = this.reservationRequest.update({
      reservationNotes: this.reservationNotes,
    });
    this.reservationRequestChange.emit(this.reservationRequest);
    // this.navigateToNext.emit();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _initForm() {
    this.formGroup = this.fb.group({
      reservationNotes: [''],
      addonServices: this.fb.array([]),
    });
  }

  resetFormData(reservationRequest: ReservationRequestItem): void {
    //
    // When doing the reset, try to keep the "Notes" part
    this.formGroup = this.fb.group({
      reservationNotes: [reservationRequest.reservationNotes !== '' ? reservationRequest.reservationNotes : this.reservationNotes],
      addonServices: this.fb.array([]),
    });

    //
    this.availableAddonServices = [];
    for (const categoryId in this.serviceCategories) {
      //
      if (this.serviceCategories.hasOwnProperty(categoryId)) {
        //
        const category: ReservationServiceCategory =
          this.serviceCategories[categoryId];

        //
        const availableList = category.availableAddonServices(
          this.reservationRequest
        );

        if (availableList.length > 0) {
          //
          this.availableAddonServices = lodash.concat(
            this.availableAddonServices,
            availableList
          );
        }
      }
    }

    // The form item for "pets"
    this.reservationRequest.selectedPets.forEach((pet) => {
      //
      // Build the array for "add-on" Services
      const serviceFormArray = [];
      this.availableAddonServices.forEach((service) => {
        //
        serviceFormArray.push(
          this.fb.group({
            id: [service.serviceId],
            name: [service.serviceName],
            description: [service.serviceDescription],
            price: [service.servicePrice],
            displayQuantityInput: [service.needQuantityInput],
            quantity: [reservationRequest.getAddonQuantity(pet.id, service.serviceId)],
            linkedService: [service],
            isSelected: [reservationRequest.includeAddonService(pet.id, service.serviceId)],
          })
        );
      });

      //
      this.addonServiceFormArray.push(
        this.fb.group({
          id: [pet.id],
          name: [`${pet.firstName} ${pet.lastName}`],
          breed: [this.getBreedNameList(pet)],
          weight: [pet.weight.weight],
          linkedPet: [pet],
          isSelected: [reservationRequest.selectedPets.length > 1 && reservationRequest.hasAddonServicesForPet(pet.id)],
          serviceFormArray: this.fb.array(lodash.cloneDeep(serviceFormArray)),
        })
      );
    });

    // Update the linked data entry.
    this.reservationRequest = this.reservationRequest.update({...reservationRequest.json()}).clone();
    this.changeDetectorRef.markForCheck();
  }

  // ----------
  // #endregion

  /////////////////////////////
}
